/* eslint-disable react/prop-types */
/**
 * DPROnePageDashboard — in-app dashboard matching the backend one-page PDF.
 * Data source: GET /dpr-log/one-page-dashboard?dprLogId={id}&shift=both
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { message } from 'antd';
import { Button, Spinner } from '@crc-evans/ui-components';
import { RefreshCw, Download, ExternalLink, FileText } from 'lucide-react';
import {
  DPR_REPORT_STATUS,
  ONE_PAGE_UI_REPORT_TYPE,
  formatDprReportGeneratedLabel,
  getStoredDprReportEntry,
  isReportReadyForDownload,
  openOrGenerateOnePageDpr,
} from '../../../services/dprReport';
import { fetchOnePageDashboard, normalizeOnePageDashboardPayload } from '../../../services/dprOnePageReportView';
import { getDprReportFilename, triggerReportDownload } from '../../../utils/reportDownloadUrl';
import { arcDashArray, LARGE_ARC_LEN, SMALL_ARC_LEN } from './arcGauge';
import './DPROnePageDashboard.css';

/* ── helpers ────────────────────────────────────────────────────────────── */
const d = (val, fb = '—') => (val == null || val === '' ? fb : String(val));

function SectionLabel({ children, color }) {
  return (
    <div className="op-sl" style={color ? { background: color } : undefined}>
      {children}
    </div>
  );
}

function SectionWrap({ children, noPad }) {
  return <div className={`op-sw${noPad ? ' op-sw--np' : ''}`}>{children}</div>;
}

/* ── gauge SVGs ─────────────────────────────────────────────────────────── */
function LargeArcGauge({ pct, color = '#087e8b', arcDash, arcLen }) {
  const pctNum = parseFloat(pct) || 0;
  const dash = arcDash ?? arcDashArray(pctNum, LARGE_ARC_LEN).dash;
  const len = arcLen ?? LARGE_ARC_LEN;
  return (
    <svg width="106" height="54" viewBox="0 0 106 54" className="op-gauge-svg">
      <path d="M8,48 A45,45 0 0,1 98,48" fill="none" stroke="#dde1e7" strokeWidth="10" strokeLinecap="round" />
      <path
        d="M8,48 A45,45 0 0,1 98,48"
        fill="none"
        stroke={color}
        strokeWidth="10"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${len}`}
      />
      <text x="53" y="40" textAnchor="middle" fontFamily="Segoe UI,Arial" fontSize="11" fontWeight="900" fill="#2b2d42">
        {d(pct, '0')} %
      </text>
    </svg>
  );
}

function SmallArcGauge({ pct, color = '#087e8b', arcDash, arcLen, capLabel }) {
  const pctNum = parseFloat(pct) || 0;
  const dash = arcDash ?? arcDashArray(pctNum, SMALL_ARC_LEN).dash;
  const len = arcLen ?? SMALL_ARC_LEN;
  return (
    <svg width="82" height="48" viewBox="0 0 82 48" className="op-gauge-svg">
      <path d="M8,41 A33,33 0 0,1 74,41" fill="none" stroke="#dde1e7" strokeWidth="9" strokeLinecap="round" />
      <path
        d="M8,41 A33,33 0 0,1 74,41"
        fill="none"
        stroke={color}
        strokeWidth="9"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${len}`}
      />
      {capLabel ? (
        <>
          <circle cx="74" cy="41" r="2.25" fill="#c81d25" />
          <text x="77" y="40" textAnchor="start" fontFamily="Segoe UI,Arial" fontSize="5.5" fontWeight="700" fill="#c81d25">
            {capLabel}%
          </text>
        </>
      ) : null}
      <text x="41" y="34" textAnchor="middle" fontFamily="Segoe UI,Arial" fontSize="8.5" fontWeight="900" fill="#2b2d42">
        {d(pct, '0')} %
      </text>
    </svg>
  );
}

function ConsumableGauge({ item }) {
  const pct = parseFloat(item.gaugeHeroVal ?? item.gaugePct ?? 0) || 0;
  const dash = item.consUsedArcDash ?? arcDashArray(pct, SMALL_ARC_LEN).dash;
  const len = item.consArcLen ?? SMALL_ARC_LEN;
  const badgeClass = item.badgeClass ?? 'ok';
  return (
    <div className="op-cons-mod">
      <div className="op-cons-name">{d(item.title ?? item.name)}</div>
      <div><span className={`op-badge op-badge--${badgeClass}`}>{d(item.badge)}</span></div>
      <svg viewBox="0 0 82 48" className="op-cons-svg" aria-hidden>
        <path d="M8,41 A33,33 0 0,1 74,41" fill="none" stroke="#dde1e7" strokeWidth="7" strokeLinecap="round" />
        <path
          d="M8,41 A33,33 0 0,1 74,41"
          fill="none"
          stroke={item.barColorUsed ?? '#087e8b'}
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${len}`}
        />
        <text x="41" y="33" textAnchor="middle" fontFamily="Segoe UI,Arial,sans-serif" fontSize="10" fontWeight="900" fill="#2b2d42">
          {d(item.gaugeHeroVal ?? Math.round(pct))}
        </text>
      </svg>
      {item.primaryGaugeLbl ? <div className="op-cons-lbl">{item.primaryGaugeLbl}</div> : null}
      {item.consumptionToday != null ? (
        <div className="op-cons-nums">Today: {item.consumptionToday}</div>
      ) : null}
    </div>
  );
}

/* ── section components ─────────────────────────────────────────────────── */
function HeaderSection({ header }) {
  if (!header) return null;
  return (
    <div className="op-hdr">
      <div className="op-hdr-left">
        <div className="op-crce-mark">CRCE</div>
        {header.clientLogoSrc ? (
          <img src={header.clientLogoSrc} alt="Client" className="op-client-logo" />
        ) : (
          <div className="op-client-tag">{d(header.clientTag, 'Client')}</div>
        )}
      </div>
      <div className="op-hdr-mid">
        <div className="op-hdr-sub">Welding Flash Report</div>
        <div className="op-hdr-title">{d(header.bundleTitle, 'Daily Progress Report')}</div>
      </div>
      <div className="op-hdr-right">
        <div className="op-hdr-rep-lbl">Report No.</div>
        <div className="op-hdr-rep-num">{d(header.bundleReportNum)}</div>
      </div>
    </div>
  );
}

const META_FIELDS = [
  ['projectNumber', 'Project Number'],
  ['client', 'Client'],
  ['finalClient', 'Final Client'],
  ['vessel', 'Vessel Name'],
  ['date', 'Date'],
  ['projectStart', 'Project Start'],
  ['expectedProjectLength', 'Expected Project Length (m)'],
  ['expectedProjectWelds', 'Expected Project Welds'],
  ['manager', 'Manager'],
  ['ongoingPipe', 'On-going Pipe'],
  ['od', 'OD (")'],
  ['wt', 'WT (mm)'],
  ['location', 'Location'],
  ['pipeStart', 'Pipe Start'],
  ['expectedPipeLength', 'Expected Pipe Length (m)'],
  ['expectedPipeWelds', 'Expected Pipe Welds'],
  ['ndtType', 'NDT Type'],
];

function MetaSection({ meta }) {
  if (!meta || typeof meta !== 'object') return null;
  return (
    <div className="op-meta">
      {META_FIELDS.map(([key, label]) => (
        <div key={key} className="op-mc">
          <div className="op-mc-l">{label}</div>
          <div className="op-mc-v">{d(meta[key])}</div>
        </div>
      ))}
    </div>
  );
}

function ProgressSection({ progress }) {
  if (!progress || typeof progress !== 'object') return null;
  return (
    <>
      <SectionLabel>Progress</SectionLabel>
      <SectionWrap>
        <div className="op-prog">
          <div className="op-gbox">
            <div className="op-glbl">% Pipe Progress</div>
            <LargeArcGauge
              pct={progress.pipeProgPct}
              color="#087e8b"
              arcDash={progress.pipeArcDash}
              arcLen={progress.pipeArcLen}
            />
            <div className="op-gauge-foot">
              NDT Accepted to date: <strong>{d(progress.progNdtAcceptedSowToDate)}</strong>
            </div>
          </div>
          <div className="op-pweld">
            <div className="op-pweld-t">Pipe Welds</div>
            <div className="op-pweld-l">NDT Accepted</div>
            <div className="op-pweld-v">{d(progress.progNdtAcceptedSowToDate)}</div>
          </div>
          <div className="op-gbox">
            <div className="op-glbl">% Project Progress</div>
            <LargeArcGauge
              pct={progress.projProgPct}
              color="#4361ee"
              arcDash={progress.projArcDash}
              arcLen={progress.projArcLen}
            />
            <div className="op-gauge-foot">
              NDT Accepted to date: <strong>{d(progress.progNdtAcceptedProjectToDate)}</strong>
            </div>
          </div>
          <div className="op-pweld">
            <div className="op-pweld-t">Project Welds</div>
            <div className="op-pweld-l">NDT Accepted</div>
            <div className="op-pweld-v">{d(progress.progNdtAcceptedProjectToDate)}</div>
          </div>
        </div>
      </SectionWrap>
    </>
  );
}

function QualitySection({ quality }) {
  if (!quality || typeof quality !== 'object') return null;
  const weldRows = Array.isArray(quality.weldLogRows) ? quality.weldLogRows : [];
  return (
    <>
      <SectionLabel>Quality</SectionLabel>
      <SectionWrap>
        <div className="op-qgrid">
          {/* Left: weld log */}
          <div>
            <div className="op-section-sublbl">Weld log — NDT repair / cut-out only (day + night)</div>
            {quality.hasWeldLogRows && weldRows.length > 0 ? (
              <table className="op-wltbl">
                <thead>
                  <tr>
                    <th style={{ width: '9%' }}>Joint</th>
                    <th style={{ width: '7%' }}>Shift</th>
                    <th style={{ width: '22%' }}>NDT / Stn1</th>
                    <th style={{ width: '16%' }}>Defect</th>
                    <th style={{ width: '7%' }}>L</th>
                    <th style={{ width: '7%' }}>D</th>
                    <th style={{ width: '7%' }}>H</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {weldRows.map((row, i) => (
                    <tr key={row.id ?? i}>
                      <td>{d(row.joint)}</td>
                      <td>{d(row.shift)}</td>
                      <td style={{ textAlign: 'left', fontSize: '10px' }}>{d(row.ndtSummary)}</td>
                      <td style={{ textAlign: 'left', fontSize: '10px' }}>{d(row.defectType)}</td>
                      <td>{d(row.length)}</td>
                      <td>{d(row.depth)}</td>
                      <td>{d(row.height)}</td>
                      <td style={{ textAlign: 'left' }}>{d(row.corrective)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="op-empty-box">No NDT repair or cut-out rows for this report date.</div>
            )}
          </div>
          {/* Right: today quality cards + defect gauges */}
          <div>
            <div className="op-section-sublbl">Today Quality (day + night)</div>
            <div className="op-qcards">
              <div className="op-qcard">
                <div className="op-qcard-l">Reweld in St1</div>
                <div className="op-qcard-v">{d(quality.qualCutOutStn1)}</div>
              </div>
              <div className="op-qcard">
                <div className="op-qcard-l">{d(quality.qualRepairLabel, 'NDT Repair')}</div>
                <div className="op-qcard-v">{d(quality.qualNdtRepair)}</div>
              </div>
              <div className="op-qcard">
                <div className="op-qcard-l">NDT Cut Out</div>
                <div className="op-qcard-v">{d(quality.qualNdtCutOut)}</div>
              </div>
            </div>
            <div className="op-qdpair">
              <div className="op-gbox">
                <div className="op-glbl">{d(quality.pipeDefectGaugeLabel, 'Pipe defect %')}</div>
                <SmallArcGauge
                  pct={quality.pipeDefPct}
                  color={quality.pipeDefGaugeColor ?? '#087e8b'}
                  capLabel={quality.ndtRejectCapLabel}
                />
                <div className="op-gauge-foot">{d(quality.pipeDefectGaugeLabel, 'Pipe defect %')}</div>
              </div>
              <div className="op-gbox">
                <div className="op-glbl">{d(quality.projectDefectGaugeLabel, 'Project defect %')}</div>
                <SmallArcGauge
                  pct={quality.projDefPct}
                  color={quality.projDefGaugeColor ?? '#4361ee'}
                  capLabel={quality.ndtRejectCapLabel}
                />
                <div className="op-gauge-foot">{d(quality.projectDefectGaugeLabel, 'Project defect %')}</div>
              </div>
            </div>
          </div>
        </div>
      </SectionWrap>
    </>
  );
}

function SafetySection({ safety }) {
  if (!safety || typeof safety !== 'object') return null;
  return (
    <>
      <SectionLabel>Safety</SectionLabel>
      <SectionWrap>
        <div className="op-safgrid">
          <div className="op-sp">
            <div className="op-sp-t">Total Incidents</div>
            <div className="op-sub2">
              <div><div className="op-snl">Today</div><div className="op-snv">{d(safety.incToday, '0')}</div></div>
              <div><div className="op-snl">To Date Project</div><div className="op-snv">{d(safety.incToDate, '0')}</div></div>
            </div>
          </div>
          <div className="op-sp">
            <div className="op-sp-t">Lost Time Injury</div>
            <div className="op-sub2">
              <div><div className="op-snl">Today</div><div className="op-snv">{d(safety.ltiToday, '0')}</div></div>
              <div><div className="op-snl">To Date Project</div><div className="op-snv">{d(safety.ltiToDate, '0')}</div></div>
            </div>
          </div>
          <div className="op-sp">
            <div className="op-sp-t">Today Tool Box Talk</div>
            <div className="op-tbtt">{d(safety.tbtToday)}</div>
          </div>
          <div className="op-sp" style={{ textAlign: 'center' }}>
            <div className="op-sp-t">To Date Project TBT</div>
            <div className="op-tbtc">{d(safety.tbtToDate)}</div>
          </div>
        </div>
      </SectionWrap>
    </>
  );
}

function DowntimeSection({ downtime }) {
  if (!downtime || typeof downtime !== 'object') return null;
  const rows = Array.isArray(downtime.rows) ? downtime.rows : [];
  return (
    <>
      <SectionLabel>Downtimes [min] — today (day + night combined)</SectionLabel>
      <SectionWrap>
        <div className="op-dt-one">
          {rows.map((row, i) => (
            <div key={row.label ?? i} className="op-dt-pair">
              <span className="op-dt-n">{d(row.label)}</span>
              <strong>{d(row.today)}</strong>
            </div>
          ))}
          <div className="op-dt-pair op-dt-pair--total">
            <span className="op-dt-n">Total today</span>
            <strong>{d(downtime.columnTotals?.today)}</strong>
          </div>
          <div className="op-dt-pair op-dt-pair--dt">
            <span className="op-dt-n">To date</span>
            <strong>{d(downtime.projectToDateMin)}</strong>
            <span className="op-dt-sub">min</span>
          </div>
        </div>
      </SectionWrap>
    </>
  );
}

function ManpowerSection({ manpower }) {
  if (!manpower || typeof manpower !== 'object') return null;
  return (
    <>
      <SectionLabel>CRCE Manpower</SectionLabel>
      <SectionWrap noPad>
        <div className="op-mpi">
          <div className="op-mps">
            <div className="op-mpl">P.O.B. per shift</div>
            <div className="op-mpr">
              <div><div className="op-mpm">Day Shift</div><div className="op-mpn">{d(manpower.pobDay)}</div></div>
              <div><div className="op-mpm">Night Shift</div><div className="op-mpn">{d(manpower.pobNight)}</div></div>
            </div>
          </div>
          <div className="op-mhb">
            <div className="op-mhl">To Date Project Man Hour</div>
            <div className="op-mhv">{d(manpower.manHoursToDate)}</div>
          </div>
        </div>
      </SectionWrap>
    </>
  );
}

function CriticalConsumablesSection({ criticalConsumables }) {
  if (!criticalConsumables || !criticalConsumables.hasData) {
    return (
      <>
        <SectionLabel>Critical Consumables</SectionLabel>
        <SectionWrap>
          <div className="op-empty-box">No critical consumable data for this report.</div>
        </SectionWrap>
      </>
    );
  }
  const leftGauges = Array.isArray(criticalConsumables.leftGauges) ? criticalConsumables.leftGauges : [];
  const rw = criticalConsumables.rightWire;

  return (
    <>
      <SectionLabel>Critical Consumables</SectionLabel>
      <SectionWrap>
        <div className="op-cc-grid">
          {/* Left gauges */}
          {leftGauges.length > 0 && (
            <div className="op-cc-left">
              <div className="op-cons-strip">
                {leftGauges.map((item, i) => (
                  <ConsumableGauge key={item.title ?? i} item={item} />
                ))}
              </div>
            </div>
          )}
          {/* Right wire section */}
          {rw && (
            <div className="op-cc-right">
              <div className="op-cc-right-title">{d(rw.title)}</div>
              {rw.actualVsPlanned && (
                <div className="op-avp">
                  <div className="op-avp-lbl">Actual vs Planned</div>
                  <div className="op-avp-row">
                    <span>Actual</span>
                    <div className="op-avp-bar-wrap">
                      <div
                        className="op-avp-bar op-avp-bar--actual"
                        style={{ width: `${Math.min(rw.actualVsPlanned.actualBarWidthPct ?? 50, 100)}%` }}
                      />
                    </div>
                    <span>{d(rw.actualVsPlanned.actualPct)}%</span>
                  </div>
                  <div className="op-avp-row">
                    <span>Planned</span>
                    <div className="op-avp-bar-wrap">
                      <div
                        className="op-avp-bar op-avp-bar--planned"
                        style={{ width: `${Math.min(rw.actualVsPlanned.plannedBarWidthPct ?? 100, 100)}%` }}
                      />
                    </div>
                    <span>{d(rw.actualVsPlanned.plannedPct)}%</span>
                  </div>
                  <span className={`op-badge op-badge--${rw.actualVsPlanned.badgeClass ?? 'ok'}`}>
                    {d(rw.actualVsPlanned.badge)}
                  </span>
                </div>
              )}
              {rw.suppliedVsNeeded && (
                <div className="op-svn">
                  <div className="op-avp-lbl">Supplied vs Needed</div>
                  <ConsumableGauge item={{ ...rw.suppliedVsNeeded, title: '', badge: '', badgeClass: 'ok' }} />
                  {rw.suppliedVsNeeded.requiredLabel ? (
                    <div className="op-cons-lbl">{rw.suppliedVsNeeded.requiredLabel}</div>
                  ) : null}
                </div>
              )}
              {rw.remainingDays && (
                <div className="op-rem">
                  <div className="op-avp-lbl">Remaining Days With Stock</div>
                  <div className="op-rem-v">{d(rw.remainingDays.value)}</div>
                  <div className="op-cons-nums">
                    Avg daily: {d(rw.remainingDays.avgDaily)} | Stock: {d(rw.remainingDays.stock)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </SectionWrap>
    </>
  );
}

function CommentsSection({ comments }) {
  if (!comments || typeof comments !== 'object') return null;
  return (
    <>
      <SectionLabel>General Comments</SectionLabel>
      <SectionWrap>
        <div className="op-comments-two">
          <div>
            <div className="op-cbox-h">Day shift</div>
            <div className="op-cbox">{d(comments.generalDay, '')}</div>
          </div>
          <div>
            <div className="op-cbox-h">Night shift</div>
            <div className="op-cbox">{d(comments.generalNight, '')}</div>
          </div>
        </div>
      </SectionWrap>
    </>
  );
}

function SignaturesSection({ signatures }) {
  if (!signatures) return null;
  const sigs = [
    signatures.approvedBy1,
    signatures.approvedBy2,
    signatures.approvedBy3,
  ].filter(Boolean);
  if (sigs.length === 0) return null;
  return (
    <>
      <SectionLabel>Signatures</SectionLabel>
      <SectionWrap>
        <div className={`op-siggrid op-siggrid--${sigs.length}`}>
          {sigs.map((sig, i) => (
            <div key={i} className="op-sigc">
              <div className="op-sigp">{d(sig.label)}</div>
              <div className="op-sig-img-wrap">
                {sig.signature ? (
                  <img className="op-sig-img" src={sig.signature} alt="" />
                ) : (
                  <span className="op-sig-ph">—</span>
                )}
              </div>
              <div className="op-sigln">
                <div className="op-sig-name">{d(sig.name)}</div>
                <div className="op-sig-pos">{d(sig.position)}</div>
              </div>
            </div>
          ))}
        </div>
      </SectionWrap>
    </>
  );
}

/* ── main view ──────────────────────────────────────────────────────────── */
const DPROnePageDashboard = ({
  dprLogId,
  shift,
  date,
  reportDetails,
  selectedProject,
  selectedPipeline,
  onReportGenerated,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dashData, setDashData] = useState(null);

  const [reportEntry, setReportEntry] = useState(() =>
    getStoredDprReportEntry(reportDetails, ONE_PAGE_UI_REPORT_TYPE)
  );
  const [isGenerating, setIsGenerating] = useState(false);

  const pdfUrl = reportEntry?.fileUrl ?? null;
  const formattedDate = moment(date).format('YYYY-MM-DD');
  const generatedLabel = formatDprReportGeneratedLabel(reportEntry);

  const load = useCallback(async () => {
    if (!dprLogId) {
      setLoadError('DPR log is not loaded yet.');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await fetchOnePageDashboard(dprLogId, { shift: 'both' });
      setDashData(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 501 || status === 503) {
        setLoadError('Dashboard API not yet available. Generate a PDF to preview below.');
      } else {
        setLoadError(err?.message || 'Failed to load one-page dashboard.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [dprLogId, shift]);

  useEffect(() => {
    setReportEntry(getStoredDprReportEntry(reportDetails, ONE_PAGE_UI_REPORT_TYPE));
  }, [reportDetails]);

  useEffect(() => { void load(); }, [load]);

  const handleGenerate = async (regenerate = false) => {
    if (!dprLogId) { message.error('DPR log not loaded.'); return; }
    setIsGenerating(true);
    try {
      const result = await openOrGenerateOnePageDpr({
        dprLogId,
        reportDetails,
        preferRegenerate: regenerate,
      });
      if (result.reportEntry) setReportEntry(result.reportEntry);
      message.success(
        result.openedFromCache ? 'One-page DPR is ready.' :
        result.fileUrl ? 'One-page DPR generated.' :
        'Generation started.'
      );
      if (onReportGenerated) onReportGenerated();
      await load();
    } catch (err) {
      message.error(err?.message || 'Failed to generate one-page DPR.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) { message.info('Generate the PDF first.'); return; }
    triggerReportDownload(pdfUrl, getDprReportFilename(ONE_PAGE_UI_REPORT_TYPE, formattedDate, shift));
    message.success('Download started.');
  };

  const handleOpenPdf = () => {
    if (!pdfUrl) { message.info('Generate the PDF first.'); return; }
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  };

  const isReady = reportEntry?.reportStatus === DPR_REPORT_STATUS.GENERATED;
  const primaryLabel = isGenerating ? 'Generating…' : isReady ? 'Regenerate PDF' : 'Generate PDF';

  /* ── header derived from normalized dashboard payload ─ */
  const viewModel = useMemo(
    () => (dashData ? normalizeOnePageDashboardPayload(dashData) : null),
    [dashData]
  );

  const header = viewModel?.header ?? null;
  const meta = viewModel?.meta ?? null;
  const progress = viewModel?.progress ?? null;
  const quality = viewModel?.quality ?? null;
  const safety = viewModel?.safety ?? null;
  const downtime = viewModel?.downtime ?? null;
  const manpower = viewModel?.manpower ?? null;
  const criticalConsumables = viewModel?.criticalConsumables ?? null;
  const comments = viewModel?.comments ?? null;
  const signatures = viewModel?.signatures ?? null;
  const hasRenderableSections = Boolean(
    viewModel && (
      header
      || progress?.pipeProgPct != null
      || meta?.projectNumber
      || quality?.hasWeldLogRows
      || quality?.pipeDefPct != null
      || safety?.incToday != null
      || (Array.isArray(downtime?.rows) && downtime.rows.length > 0)
      || manpower?.pobDay != null
    )
  );

  return (
    <div className="dpr-one-page-panel">
      <div className="dpr-one-page-card">
        <div className="dpr-one-page-card-head">
          <div className="dpr-one-page-card-head-left">
            <div className="dpr-one-page-card-icon" aria-hidden>
              <FileText size={24} />
            </div>
            <div>
              <h3 className="dpr-one-page-card-title">One-Page DPR Summary</h3>
              <p className="dpr-one-page-card-desc">
                Live dashboard view matching the one-page PDF report. Generate or download the PDF without submitting Comments.
              </p>
              {isReady && generatedLabel !== 'A report is already saved for this type.' ? (
                <p className="dpr-one-page-card-meta">{generatedLabel}</p>
              ) : null}
            </div>
          </div>
          <div className="dpr-one-page-card-actions">
            <button
              type="button"
              className="dpr-one-page-icon-btn"
              onClick={load}
              disabled={isLoading}
              title="Refresh"
              aria-label="Refresh one-page dashboard"
            >
              <RefreshCw size={16} />
            </button>
            <Button
              label={primaryLabel}
              variant="primary"
              size="medium"
              onClick={() => handleGenerate(isReady)}
              disabled={isGenerating || isLoading}
            />
            <Button
              label="Download PDF"
              variant="secondary"
              size="medium"
              icon={Download}
              onClick={handleDownload}
              disabled={!pdfUrl || isGenerating}
            />
            <Button
              label="Open PDF"
              variant="tertiary"
              size="medium"
              icon={ExternalLink}
              onClick={handleOpenPdf}
              disabled={!pdfUrl || isGenerating}
            />
          </div>
        </div>

        {loadError ? (
          <div className="dpr-one-page-banner dpr-one-page-banner--warn">
            {loadError}
            {pdfUrl ? (
              <button type="button" className="dpr-one-page-banner-link" onClick={handleOpenPdf}>
                Open saved PDF
              </button>
            ) : null}
          </div>
        ) : null}

        {isLoading ? (
          <div className="dpr-one-page-loading">
            <Spinner variant="inline" size="medium" />
            <span>Loading one-page report…</span>
          </div>
        ) : null}

        {!isLoading && viewModel && hasRenderableSections ? (
          <div className="op-doc">
            <HeaderSection header={header} />
            <MetaSection meta={meta} />
            <ProgressSection progress={progress} />
            <QualitySection quality={quality} />
            <SafetySection safety={safety} />
            <DowntimeSection downtime={downtime} />

            <div className="op-two">
              <div><ManpowerSection manpower={manpower} /></div>
              <div><CriticalConsumablesSection criticalConsumables={criticalConsumables} /></div>
            </div>

            <CommentsSection comments={comments} />
            <SignaturesSection signatures={signatures} />
          </div>
        ) : null}

        {!isLoading && (!viewModel || !hasRenderableSections) && !loadError ? (
          <div className="dpr-one-page-loading">
            <p>No preview available yet.</p>
            <Button
              label="Generate PDF"
              variant="primary"
              size="medium"
              onClick={() => handleGenerate(false)}
              disabled={isGenerating}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DPROnePageDashboard;
